import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CaretDownIcon,
  FlowArrowIcon,
  LightbulbIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { PlanLearningsNote } from './PlanLearningsNote';

import { getSuggestedAudiencesFor } from '@/components/discover/preferences-taxonomy';
import { ProjectIconPicker } from '@/components/projects/ProjectIconPicker';
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from '@/components/ui/autocomplete';
import { BrandKitPicker } from '@/components/ui/brand-kit-picker';
import { BrandVoicePicker } from '@/components/ui/brand-voice-picker';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  CampaignLineIcon,
  FillCalendarLineIcon,
  ScheduleLineIcon,
  TemplateLineIcon,
} from '@/components/ui/illustrations';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useCreatePlan } from '@/hooks/plans/useCreatePlan';
import type { CreatePlanEntry } from '@/hooks/plans/useCreatePlan';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useBrandVoices } from '@/hooks/useBrandVoices';
import { useBusinessContext } from '@/hooks/useBusinessContext';
import type { VoiceMode } from '@/lib/ai-agent/prompts/voice-injection';
import type { StoredProjectStrategy } from '@/lib/projects/project-strategy';
import { CHOOSER_OPTION_ROW, ITEM_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { campaignService } from '@/services/data/CampaignService';
import type { ProjectKind } from '@/services/data/CampaignService';

type DialogView = 'chooser' | 'manual';
type DialogMode = 'create' | 'edit';

interface CreatePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialView?: DialogView;
  entry?: CreatePlanEntry;
  mode?: DialogMode;
  planId?: string;
  initialName?: string;
  initialGoal?: string;
  initialIcon?: string | null;
  initialColor?: string | null;
  initialBrandVoiceId?: string | null;
  initialBrandKitId?: string | null;
  initialStrategy?: StoredProjectStrategy | null;
}

export const PROJECT_STARTERS: Array<{
  kind: ProjectKind;
  label: string;
  sub: string;
  seedPrompt: string;
  illustration: typeof ScheduleLineIcon;
}> = [
  {
    kind: 'launch',
    label: 'Plan a launch',
    sub: 'Multi-platform launch with calendar slots',
    seedPrompt: 'Plan a product launch campaign',
    illustration: ScheduleLineIcon,
  },
  {
    kind: 'growth',
    label: 'Grow my following',
    sub: 'Cadence-based posting over weeks',
    seedPrompt: 'Help me grow my audience with a steady posting cadence',
    illustration: CampaignLineIcon,
  },
  {
    kind: 'campaign',
    label: 'Plan open slots',
    sub: 'Create posts for gaps in your schedule',
    seedPrompt: 'Create posts for the open slots in my schedule for the next 2 weeks',
    illustration: FillCalendarLineIcon,
  },
  {
    kind: 'custom',
    label: 'Set up manually',
    sub: 'Custom project with a goal',
    seedPrompt: '',
    illustration: TemplateLineIcon,
  },
];

// Manual setup is a distinct decision, not a fourth starter workflow. Keeping
// it in the footer avoids saying the same thing twice and gives the template
// action a stable visual counterweight.
const PROJECT_CHOOSER_STARTERS = PROJECT_STARTERS.filter((starter) => starter.kind !== 'custom');

/**
 * Two-view project creation dialog:
 *   - 'chooser' (default): 3 starter cards + 'Set up manually' link
 *   - 'manual': compact identity + name in create mode; deliberate project
 *     context disclosures in edit mode.
 *
 * Manual view design (per tasks/2026-04-28-projects-redesign.md):
 *   - Title only — no subtitle. Editing is self-explanatory.
 *   - Create mode asks only for project identity and name.
 *   - Edit mode owns description, audience focus, and advanced defaults.
 *   - Instructions stay on the project detail surface where their effect is
 *     visible; the dialog does not duplicate that editor.
 */
export function CreatePlanDialog({
  open,
  onOpenChange,
  initialView = 'chooser',
  entry,
  mode = 'create',
  planId,
  initialName,
  initialGoal,
  initialIcon,
  initialColor,
  initialBrandVoiceId,
  initialBrandKitId,
  initialStrategy,
}: CreatePlanDialogProps) {
  const createPlan = useCreatePlan();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Workspace-first: the active kit's own resolved business type, with the
  // shared account `company_type` only as `getBrandFacts()`'s last resort —
  // never the account classification directly (settled workspace-tenancy
  // model, 2026-08-23; an agency account can hold differently typed
  // client workspaces).
  const { businessContext } = useBusinessContext();
  const businessType = businessContext?.businessType;
  const { brandVoices } = useBrandVoices();
  const { kits: brandKits } = useBrandKits();
  const isEdit = mode === 'edit';
  const effectiveInitialView: DialogView = isEdit ? 'manual' : initialView;
  const [view, setView] = useState<DialogView>(effectiveInitialView);
  const [name, setName] = useState(initialName ?? '');
  const [goal, setGoal] = useState(initialGoal ?? '');
  const [icon, setIcon] = useState<string | null>(initialIcon ?? null);
  const [color, setColor] = useState<string | null>(initialColor ?? null);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>({ kind: 'brand' });
  const [activeBrandVoiceId, setActiveBrandVoiceId] = useState<string | null>(
    initialBrandVoiceId ?? null,
  );
  const [activeBrandKitId, setActiveBrandKitId] = useState<string | null>(
    initialBrandKitId ?? null,
  );
  const [saving, setSaving] = useState(false);
  const [descOpen, setDescOpen] = useState(false);

  const [audienceFocus, setAudienceFocus] = useState(initialStrategy?.audience ?? '');
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [audienceSuggestionsOpen, setAudienceSuggestionsOpen] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const audienceInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const audienceLabelId = useId();
  const audienceHelpId = useId();
  const audienceErrorId = useId();
  const suggestedAudiences = getSuggestedAudiencesFor(businessType);

  useEffect(() => {
    if (open) {
      setView(effectiveInitialView);
      setName(initialName ?? '');
      setGoal(initialGoal ?? '');
      setIcon(initialIcon ?? null);
      setColor(initialColor ?? null);
      setVoiceMode({ kind: 'brand' });
      setActiveBrandVoiceId(initialBrandVoiceId ?? null);
      setActiveBrandKitId(initialBrandKitId ?? null);
      setDescOpen(false);
      setAudienceFocus(initialStrategy?.audience ?? '');
      setAudienceOpen(false);
      setAudienceSuggestionsOpen(false);
      setAudienceError(null);
    }
  }, [
    open,
    effectiveInitialView,
    initialName,
    initialGoal,
    initialIcon,
    initialColor,
    initialBrandVoiceId,
    initialBrandKitId,
    initialStrategy?.audience,
  ]);

  const handleStarterClick = (starter: (typeof PROJECT_STARTERS)[number]) => {
    if (starter.kind === 'custom') {
      setView('manual');
      return;
    }
    createPlan.mutate(
      { seedPrompt: starter.seedPrompt, kind: starter.kind, entry: entry ?? 'starter_card' },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const canSubmitManual = name.trim().length > 0 && !createPlan.isPending && !saving;

  const handleManualSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canSubmitManual) return;
    if (isEdit && planId) {
      const audienceValue = audienceFocus.trim();
      if (audienceValue.length > 0 && audienceValue.length < 10) {
        setAudienceOpen(true);
        setAudienceError('Enter at least 10 characters or leave blank.');
        requestAnimationFrame(() => audienceInputRef.current?.focus());
        return;
      }
      setSaving(true);
      setAudienceSuggestionsOpen(false);
      try {
        // Audience persists into strategy.audience — the field the composer's
        // PLAN CONTEXT digest reads (buildStrategyDigest). Before 2026-07-09
        // the audience control was UI-only: edits were silently dropped on Save. Only
        // send strategy when the audience actually changed, so an untouched
        // save can't clobber concurrent strategy writes or trigger the
        // draft→active auto-promotion for free.
        const initialAudience = initialStrategy?.audience?.trim() ?? '';
        const audienceChanged = audienceValue !== initialAudience;
        const res = await campaignService.updateCampaign(planId, {
          name: name.trim(),
          goal: goal.trim() || undefined,
          icon,
          color,
          brandVoiceId: activeBrandVoiceId,
          brandKitId: activeBrandKitId,
          ...(audienceChanged
            ? {
                // Partial stored-strategy merge preserves generated siblings.
                // Empty audience ('') clears the project focus; the agent
                // digest then falls back to the account's brand audience.
                strategy: {
                  ...(initialStrategy ?? {}),
                  audience: audienceValue,
                } satisfies StoredProjectStrategy,
              }
            : {}),
        });
        if (res.error) {
          toast.error(`Couldn't save: ${res.error}`);
          return;
        }
        await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        toast.success('Project updated');
        onOpenChange(false);
      } finally {
        setSaving(false);
      }
      return;
    }
    createPlan.mutate(
      {
        name,
        goal: goal || undefined,
        kind: 'custom',
        entry: entry ?? 'manual_dialog',
        icon,
        color,
        brandVoiceId: activeBrandVoiceId,
        brandKitId: activeBrandKitId,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  const handleResetIdentity = () => {
    setIcon(null);
    setColor(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} size={view === 'chooser' ? 'default' : 'sm'}>
        {view === 'chooser' ? (
          <>
            <DialogHeader>
              <DialogTitle>Create a project</DialogTitle>
              <DialogDescription>
                Pick a starting point and Mora will draft the strategy. Or set it up manually.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              {PROJECT_CHOOSER_STARTERS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => handleStarterClick(s)}
                  disabled={createPlan.isPending}
                  className={CHOOSER_OPTION_ROW}
                >
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-md bg-muted/30 text-foreground/85 group-hover:bg-muted/50 transition-colors">
                    <s.illustration className="size-9" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={cn('block', ITEM_TITLE)}>{s.label}</span>
                    <span className="block text-detail text-muted-foreground/70 truncate">
                      {s.sub}
                    </span>
                  </span>
                  <ArrowRightIcon
                    className="size-3.5 text-muted-foreground/40 group-hover:text-foreground/60 transition-colors shrink-0"
                    weight="regular"
                  />
                </button>
              ))}
            </div>

            <PlanLearningsNote brandKitId={activeBrandKitId} />

            <DialogFooter className="sm:justify-between">
              <button
                type="button"
                onClick={() => setView('manual')}
                disabled={createPlan.isPending}
                className={cn(
                  'text-detail text-muted-foreground/70 hover:text-foreground/85 transition-colors',
                  'inline-flex items-center gap-1 underline-offset-4 hover:underline',
                  createPlan.isPending && 'opacity-60 cursor-wait',
                )}
              >
                Set up manually <ArrowRightIcon className="size-3" weight="bold" />
              </button>
              <div className="flex items-center gap-3">
                {createPlan.isPending && (
                  <span className="inline-flex items-center gap-1.5 text-detail text-muted-foreground/70">
                    <Spinner className="size-3" />
                    Creating…
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    navigate('/projects#workflow-templates');
                  }}
                  disabled={createPlan.isPending}
                  className={cn(
                    'text-detail text-muted-foreground/70 hover:text-foreground/85 transition-colors',
                    'inline-flex items-center gap-1 underline-offset-4 hover:underline',
                    createPlan.isPending && 'opacity-60 cursor-wait',
                  )}
                >
                  <FlowArrowIcon className="size-3" weight="regular" />
                  Browse templates
                </button>
              </div>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {!isEdit && (
                  <CircleIconButton
                    ariaLabel="Back to templates"
                    size="sm"
                    className="-ml-2 shrink-0 hover:bg-muted"
                    onClick={() => setView('chooser')}
                  >
                    <ArrowLeftIcon className="size-4" weight="regular" />
                  </CircleIconButton>
                )}
                <span className="truncate">{isEdit ? 'Edit project' : 'Create project'}</span>
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleManualSubmit} className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="project-name" spacing="none">
                  Project name
                </Label>
                <InputGroup data-disabled={createPlan.isPending || saving ? true : undefined}>
                  <InputGroupInput
                    id="project-name"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name your project"
                    maxLength={140}
                    disabled={createPlan.isPending || saving}
                  />
                  <InputGroupAddon align="inline-start">
                    <ProjectIconPicker
                      variant="inline"
                      currentIcon={icon}
                      currentColor={color}
                      onIconSelect={setIcon}
                      onColorSelect={setColor}
                      onReset={handleResetIdentity}
                      disabled={createPlan.isPending || saving}
                    />
                  </InputGroupAddon>
                </InputGroup>
              </div>

              {!isEdit && (
                <div
                  role="note"
                  className="flex items-start gap-2.5 rounded-xl bg-muted/60 px-3 py-2.5 text-detail text-muted-foreground"
                >
                  <LightbulbIcon
                    className="mt-0.5 size-4 shrink-0 text-foreground/65"
                    weight="regular"
                  />
                  <p>
                    Projects keep chats, Context, and custom instructions together for ongoing work.
                  </p>
                </div>
              )}

              {isEdit && (
                <Collapsible open={descOpen} onOpenChange={setDescOpen}>
                  <CollapsibleTrigger
                    className={cn(
                      'group flex items-center gap-1 text-caption font-medium text-muted-foreground/80',
                      'hover:text-foreground transition-colors -mx-1 px-1 py-1 rounded-sm',
                      'focus-visible:outline-none focus-visible:bg-muted/40',
                    )}
                  >
                    <CaretDownIcon
                      className={cn(
                        'size-3 transition-transform duration-200',
                        descOpen ? 'rotate-0' : '-rotate-90',
                      )}
                      weight="regular"
                    />
                    {goal ? 'Description' : 'Add a description'}
                    <span className="font-normal text-muted-foreground/60">(optional)</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <Textarea
                      size="sm"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      placeholder="What are you trying to achieve?"
                      rows={3}
                      maxLength={500}
                      className="mt-1.5"
                      disabled={createPlan.isPending || saving}
                    />
                  </CollapsibleContent>
                </Collapsible>
              )}

              {isEdit && (
                <Collapsible open={audienceOpen} onOpenChange={setAudienceOpen}>
                  <CollapsibleTrigger
                    className={cn(
                      'group flex items-center gap-1 text-caption font-medium text-muted-foreground/80',
                      'hover:text-foreground transition-colors -mx-1 px-1 py-1 rounded-sm',
                      'focus-visible:outline-none focus-visible:bg-muted/40',
                    )}
                  >
                    <CaretDownIcon
                      className={cn(
                        'size-3 transition-transform duration-200',
                        audienceOpen ? 'rotate-0' : '-rotate-90',
                      )}
                      weight="regular"
                    />
                    <span id={audienceLabelId}>Audience focus</span>
                    <span className="font-normal text-muted-foreground/60">(optional)</span>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-1.5 space-y-1.5">
                      <p id={audienceHelpId} className="text-detail text-muted-foreground/70">
                        Who this project should speak to. Leave blank to use your brand audience.
                      </p>
                      <Autocomplete
                        items={suggestedAudiences}
                        value={audienceFocus}
                        modal={false}
                        open={audienceSuggestionsOpen}
                        onOpenChange={setAudienceSuggestionsOpen}
                        onValueChange={(next: string) => {
                          setAudienceFocus(next);
                          if (next.trim().length === 0 || next.trim().length >= 10) {
                            setAudienceError(null);
                          }
                        }}
                        openOnInputClick
                      >
                        <AutocompleteInput
                          ref={audienceInputRef}
                          aria-labelledby={audienceLabelId}
                          aria-describedby={
                            audienceError ? `${audienceHelpId} ${audienceErrorId}` : audienceHelpId
                          }
                          aria-invalid={audienceError ? true : undefined}
                          placeholder="Describe or choose an audience…"
                          minLength={10}
                          maxLength={400}
                          disabled={createPlan.isPending || saving}
                          onBlur={() => {
                            const length = audienceFocus.trim().length;
                            if (length > 0 && length < 10) {
                              setAudienceError('Enter at least 10 characters or leave blank.');
                            }
                          }}
                          onInvalid={(event) => {
                            event.preventDefault();
                            setAudienceError('Enter at least 10 characters or leave blank.');
                          }}
                        />
                        <AutocompleteContent portalContainer={dialogContentRef}>
                          <AutocompleteEmpty>
                            No matching suggestions. Keep typing to use your own.
                          </AutocompleteEmpty>
                          <AutocompleteList>
                            {(item: string) => (
                              <AutocompleteItem key={item} value={item}>
                                {item}
                              </AutocompleteItem>
                            )}
                          </AutocompleteList>
                        </AutocompleteContent>
                      </Autocomplete>
                      {audienceError ? (
                        <p
                          id={audienceErrorId}
                          role="alert"
                          className="text-detail text-destructive"
                        >
                          {audienceError}
                        </p>
                      ) : null}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {isEdit && (
                <details className="group rounded-lg border border-border/40 bg-card/40">
                  <summary
                    className={cn(
                      'flex cursor-pointer list-none items-center justify-between px-4 py-3 [&::-webkit-details-marker]:hidden',
                      ITEM_TITLE,
                    )}
                  >
                    Advanced
                    <CaretDownIcon
                      className="size-3.5 text-muted-foreground/70 transition-transform group-open:rotate-180"
                      weight="regular"
                    />
                  </summary>
                  <div className="border-t border-border/40 px-4 py-4">
                    <Label className="text-detail text-muted-foreground">Brand voice</Label>
                    <div className="mt-1.5">
                      <BrandVoicePicker
                        value={voiceMode}
                        activeBrandVoiceId={activeBrandVoiceId}
                        voices={brandVoices}
                        offAllowed={false}
                        onChange={({ mode, brandVoiceId }) => {
                          setVoiceMode(mode);
                          setActiveBrandVoiceId(brandVoiceId);
                        }}
                      />
                    </div>
                    <p className="mt-2 text-detail text-muted-foreground">
                      Defaults to your default voice if you don&apos;t pick one.
                    </p>

                    <Label className="mt-4 text-detail text-muted-foreground">Brand kit</Label>
                    <div className="mt-1.5">
                      <BrandKitPicker
                        value={activeBrandKitId}
                        kits={brandKits.map((k) => ({
                          id: k.id,
                          name: k.name,
                          is_default: k.is_default,
                          primary_color: k.primary_color,
                          secondary_color: k.secondary_color,
                        }))}
                        onChange={(kitId) => setActiveBrandKitId(kitId)}
                        offAllowed={false}
                      />
                    </div>
                    <p className="mt-2 text-detail text-muted-foreground">
                      Defaults to your default kit if you don&apos;t pick one.
                    </p>
                  </div>
                </details>
              )}

              <DialogFooter className="pt-1">
                {isEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenChange(false)}
                    disabled={createPlan.isPending || saving}
                  >
                    Cancel
                  </Button>
                )}
                <Button type="submit" variant="neutral" size="sm" disabled={!canSubmitManual}>
                  {createPlan.isPending || saving ? (
                    <>
                      <Spinner className="size-3.5" />
                      {isEdit ? 'Saving…' : 'Creating…'}
                    </>
                  ) : isEdit ? (
                    'Save'
                  ) : (
                    'Create project'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
